import {
  useGetTeamsQuery,
  useUpdateTeamMutation,
} from 'store/slices/apiV1/properties';
import { BooleanIconRenderCell } from 'ui-component/DataGrid/Render';

import { ALL_APP_IDS, ALL_APPS } from 'constants/appConstants';
import {
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { orgFromUserOrgId } from 'utils/userPermissions';
import { useMemo, useState } from 'react';
import _ from 'lodash';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { useSelector } from 'store';
import { ExtendedGridColDef, DatagridNames, RowAction } from 'types/datagrid';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import { appPermissionAccessLevels } from 'types/apps';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import CreateTeamDialog from 'views/properties/Teams/components/CreateTeamDialog';
import useGetCustomFieldColumns from 'hooks/useGetCustomFieldColumns';
import { RecordTypes } from 'ui-component/DetailCustomFields';
import { IconArchive, IconArchiveOff, IconPencil } from '@tabler/icons-react';
import { Team } from 'types/properties';
import UpdateTeamDialog from 'views/properties/Teams/components/UpdateTeamDialog';
import { EditInlineTeamTypeAutocomplete } from 'ui-component/HookFormComponents/InlineEditInputComponents';
import { GridRenderEditCellParams } from '@mui/x-data-grid';
import { FieldValues } from 'react-hook-form';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import ArchiveDialog from 'ui-component/ArchiveDialog';
import { ManagementActionTypes } from 'types';
import { useDialogManager } from 'hooks/useDialogManager';

enum TEAMS_DIALOGS {
  CREATE = 'create',
  UPDATE = 'update',
  ARCHIVE = 'archive',
}

const TeamsTable = () => {
  const { data: user } = useGetUserQuery(undefined);
  const { activeOrgId } = useSelector((state) => state.org);

  const [teamToUpdate, setTeamToUpdate] = useState<Team | null>(null);
  const { openDialog, closeDialog, currentDialog } = useDialogManager(
    Object.values(TEAMS_DIALOGS)
  );

  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();
  const { data: teams, isLoading: isLoadingTeams } = useGetTeamsQuery();

  const otherOrgs = useMemo(
    () =>
      _.filter(
        teams,
        (team) =>
          (typeof team.org === 'object' ? team.org.id : team.org) !==
          activeOrgId
      ).length,
    [activeOrgId, teams]
  );

  const { isPermissionDisabled } = useAppAccessContext();
  const editDisabled = isPermissionDisabled({
    app: ALL_APP_IDS.PROPERTIES,
    accessLevel: appPermissionAccessLevels.edit,
  });

  const handleOpenUpdateTeamDialog = (rowTeam: Team) => {
    openDialog(TEAMS_DIALOGS.UPDATE);
    setTeamToUpdate(rowTeam);
  };

  const handleOpenArchiveDialog = (rowTeam: Team) => {
    openDialog(TEAMS_DIALOGS.ARCHIVE);
    setTeamToUpdate(rowTeam);
  };

  const { customFieldColumns } = useGetCustomFieldColumns({
    recordType: RecordTypes.team,
  });

  const columns = useMemo<ExtendedGridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        description: handleReplaceWithCustomTeamName('Name of this team'),
        type: 'string',
        flex: 0.5,
        editable: true,
        inlineEditRenderCell: true,
        useMutation: useUpdateTeamMutation,
        editPermissionScope: {
          app: ALL_APP_IDS.PROPERTIES,
          accessLevel: appPermissionAccessLevels.edit,
        },
        permissionScope: {
          app: ALL_APP_IDS.PROPERTIES,
        },
      },
      {
        field: 'typeName',
        headerName: handleReplaceWithCustomTeamName('Team Type'),
        description: handleReplaceWithCustomTeamName('Type of this team'),
        type: 'string',
        flex: 0.4,
        editable: true,
        inlineEditRenderCell: true,
        useMutation: useUpdateTeamMutation,
        mutationInputElement: EditInlineTeamTypeAutocomplete,
        getMutationDefaultValue: (params: GridRenderEditCellParams) =>
          params.row.typeId,
        mutationPreSubmit: (data: FieldValues) => ({
          teamType: _.get(data, 'typeName'),
        }),
        valueGetter: (params: GridValueGetterParams) =>
          params.row.teamType?.name,
        editPermissionScope: {
          app: ALL_APP_IDS.PROPERTIES,
          accessLevel: appPermissionAccessLevels.edit,
        },
        permissionScope: {
          app: ALL_APP_IDS.PROPERTIES,
        },
      },
      ...(otherOrgs > 0
        ? [
            {
              field: 'org',
              headerName: 'Org',
              description: handleReplaceWithCustomTeamName(
                'Org associated with this team'
              ),
              type: 'string',
              flex: 0.5,
              valueGetter: (params: GridValueGetterParams) =>
                orgFromUserOrgId(user, params.row.org)?.name,
            },
          ]
        : []),
      {
        field: 'industry',
        headerName: 'End-Use Industry',
        description: 'End-use industry for this team',
        type: 'string',
        flex: 0.4,
        hideable: true,
        valueGetter: (params: GridValueGetterParams) =>
          params.row.industry ?? null,
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      {
        field: 'militaryUse',
        headerName: 'Military End-Use',
        description: 'Military end-use for this team',
        type: 'boolean',
        flex: 0.3,
        hideable: true,
        valueGetter: (params: GridValueGetterParams) =>
          params.row.militaryUse ?? false,
        renderCell: (params: GridRenderCellParams) =>
          BooleanIconRenderCell(params),
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      {
        field: 'dpas',
        headerName: 'DPAS Rating',
        description: 'DPAS rating for this team',
        type: 'string',
        flex: 0.3,
        hideable: true,
        valueGetter: (params: GridValueGetterParams) => params.row.dpas ?? null,
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      {
        field: 'defaultCofactrWarehouse',
        headerName: 'Default Cofactr Warehouse',
        description: 'Default Cofactr warehouse for this team',
        type: 'string',
        flex: 0.4,
        hideable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const loc = params.row.defaultCofactrWarehouse;
          return loc == null
            ? '—'
            : typeof loc === 'object'
            ? loc.externalName ?? loc.name ?? loc.id
            : loc;
        },
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      {
        field: 'defaultShippingCarrier',
        headerName: 'Default Shipping Carrier',
        description: 'Default shipping carrier for this team',
        type: 'string',
        flex: 0.35,
        hideable: true,
        valueGetter: (params: GridValueGetterParams) =>
          params.row.defaultShippingCarrier ?? null,
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      {
        field: 'defaultShippingService',
        headerName: 'Default Shipping Service',
        description: 'Default shipping service for this team',
        type: 'string',
        flex: 0.35,
        hideable: true,
        valueGetter: (params: GridValueGetterParams) =>
          params.row.defaultShippingService ?? null,
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      {
        field: 'defaultShippingDestinationLocation',
        headerName: 'Default Shipping Destination',
        description: 'Default shipping destination location for this team',
        type: 'string',
        flex: 0.4,
        hideable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const loc = params.row.defaultShippingDestinationLocation;
          return loc == null
            ? '—'
            : typeof loc === 'object'
            ? loc.externalName ?? loc.name ?? loc.id
            : loc;
        },
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      {
        field: 'active',
        headerName: 'Active',
        description: handleReplaceWithCustomTeamName(
          'Indicates whether this team is active'
        ),
        type: 'boolean',
        flex: 0.3,
        hideable: true,
        valueGetter: (params: GridValueGetterParams) => !params.row.archived,
        renderCell: (params: GridRenderCellParams) =>
          BooleanIconRenderCell(params),
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      ...customFieldColumns,
    ],
    [customFieldColumns, otherOrgs, user, handleReplaceWithCustomTeamName]
  );

  return (
    <>
      <ExtendedDatagrid<Team>
        loading={isLoadingTeams}
        noRows={handleReplaceWithCustomTeamName('No Teams found.')}
        gridName={DatagridNames.teams}
        rows={teams ?? []}
        columns={columns}
        fullCard
        initialVisibleColumns={[
          'name',
          'typeName',
          'org',
          ..._.map(customFieldColumns, 'field'),
        ]}
        initialFilter={{
          filterModel: {
            items: [
              {
                columnField: 'active',
                operatorValue: 'is',
                value: 'true',
              },
            ],
          },
        }}
        editable
        editLocked={editDisabled}
        newRecordButton={{
          label: handleReplaceWithCustomTeamName('New Team'),
          onClick: () => openDialog(TEAMS_DIALOGS.CREATE),
        }}
        rowActions={
          [
            {
              Icon: IconPencil,
              getLabel: () => handleReplaceWithCustomTeamName('Edit Team'),
              permissionScope: {
                app: ALL_APPS.PROPERTIES.id,
                accessLevel: appPermissionAccessLevels.edit,
              },
              getRowTooltip: () =>
                handleReplaceWithCustomTeamName('Update this team'),
              color: 'primary',
              disabled: editDisabled,
              onRowClick: (row) => {
                handleOpenUpdateTeamDialog(row as Team);
              },
            },
            {
              getRowIcon: (row) =>
                row?.archived ? IconArchiveOff : IconArchive,
              getLabel: (row) =>
                handleReplaceWithCustomTeamName(
                  row?.archived ? 'Mark Active' : 'Mark Inactive'
                ),
              permissionScope: {
                app: ALL_APPS.PROPERTIES.id,
                accessLevel: appPermissionAccessLevels.edit,
              },
              getRowTooltip: (row) =>
                handleReplaceWithCustomTeamName(
                  `Mark this team ${row?.archived ? 'active' : 'inactive'}`
                ),
              color: 'primary',
              disabled: editDisabled,
              onRowClick: (row) => {
                handleOpenArchiveDialog(row as Team);
              },
            },
          ] as RowAction<Team>[]
        }
      />
      <CreateTeamDialog
        dialogOpen={currentDialog === TEAMS_DIALOGS.CREATE}
        onClose={closeDialog}
      />
      {teamToUpdate && (
        <UpdateTeamDialog
          dialogOpen={currentDialog === TEAMS_DIALOGS.UPDATE}
          onClose={closeDialog}
          teamToUpdate={teamToUpdate}
        />
      )}
      {teamToUpdate && (
        <ArchiveDialog
          idToUpdate={teamToUpdate.id}
          archived={teamToUpdate.archived}
          dialogOpen={currentDialog === TEAMS_DIALOGS.ARCHIVE}
          onClose={closeDialog}
          actionType={ManagementActionTypes.team}
        />
      )}
    </>
  );
};

export default TeamsTable;
